import { DatePipe, DecimalPipe, Location } from "@angular/common";
import {
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  ViewChild,
} from "@angular/core";
import { NgForm } from "@angular/forms";
import { ActivatedRoute, NavigationEnd, Router } from "@angular/router";
import { DateTime } from "luxon";
import { of, throwError } from "rxjs";
import { catchError, flatMap, map } from "rxjs/operators";
import { conformToMask } from "text-mask-core";
import { ExpenseType } from "./../../model/domain/ExpenseType";

import { faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import { environment } from "../../../environments/environment";
import { TicketCutoffTypeEnum } from "../../enum/cutoff-type.enum";
import { MenuConstants } from "../../infrastructure/MenuConstants";
import { Bank } from "../../model/domain/Bank";
import {
  DialogSize,
  OperationType,
  Portfolio,
  SettlementType,
  TicketStatus,
  TicketType,
} from "../../model/domain/Entity";
import { Expenses } from "../../model/domain/Expenses";
import { Favored } from "../../model/domain/Favored";
import { NamedType } from "../../model/domain/Tipo";
import { AuthService } from "../../services/auth.service";
import { CutoffService } from "../../services/cutoff.service";
import { DialogService } from "../../services/dialog.service";
import { DomainService } from "../../services/domain.service";
import { FileService } from "../../services/file.service";
import { LoaderService } from "../../services/loader.service";
import { SnackbarService } from "../../services/snackbar.service";
import { TicketService } from "../../services/ticket.service";
import { ValidatorService } from "../../services/validator.service";
import { AnnotationComponent } from "../annotation/annotation.component";
import { Roles } from "../auth/auth.role";
import { TicketCutoffPermission } from "../cutoff/ticket-cutoff-type.model";
import { DadosFundoComponent } from "../dados-fundo/dados-fundo.component";
import { FavoredComponent } from "../favored/favored.component";

@Component({
  selector: "app-expenses",
  templateUrl: "./expenses.component.html",
  styleUrls: ["./expenses.component.css"],
})
export class ExpensesComponent implements OnInit, OnDestroy {
  public model: Expenses = null;

  faInfoCircle = faInfoCircle;

  navigationSubscription;

  isEditing = false;
  canValidate = false;
  isSubmit = false;

  decimalPipe = new DecimalPipe("pt");

  @ViewChild("thisForm", { static: true }) public frm: NgForm;
  @ViewChild("portfolio", { static: true }) fundoComponent: DadosFundoComponent;
  @ViewChild("annotation", { static: true })
  annotationComponent: AnnotationComponent;
  @ViewChild("favored", { static: true }) favoredComponent: FavoredComponent;

  TicketTypeEnum = TicketType;
  dataChanged: EventEmitter<string>;
  expensesType: ExpenseType[];
  banksList: Bank[] = null;
  hasPropositionIsDisabled = false;
  ticketService: any;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private service: TicketService,
    public validator: ValidatorService,
    private datePipe: DatePipe,
    private authService: AuthService,
    private dialogService: DialogService,
    private snackBarService: SnackbarService,
    private location: Location,
    private loaderService: LoaderService,
    private domainService: DomainService,
    private cutoffService: CutoffService,
    private fileService: FileService
  ) {
    this.navigationSubscription = this.router.events.subscribe((e: any) => {
      if (e instanceof NavigationEnd) {
        if (!this.isSubmit) {
          this.resetForm();
        }
      }
    });
  }

  settlementType = SettlementType;
  ListSettlementTypes: NamedType<number>[];
  isNotProduction = environment.name != 'production';

  validators = {
    operationValue: (value) => (value) => this.coalesce(value) > 0,
    unitPrice: (value) =>
      this.coalesce(value).length === 0 ||
      conformToMask(value, this.validator.unitPriceMask, {}),
    issueDate: (value) =>
      this.coalesce(value).length === 0 ||
      DateTime.fromFormat(value, "dd/MM/yyyy").isValid,
    settlementDate: (value) => this.model.settlementDate != null && value != "",
    expenseTypeId: (value) => this.coalesce(value) > 0,
    favoredDocument: (value) =>
      this.coalesce(value).length === 0 ||
      this.validator.validateDocument(value),
  };

  ngOnInit() {
    this.isSubmit = false;
    this.dataChanged = new EventEmitter<string>();
    this.getTicket();
  }

  ngOnDestroy() {
    if (this.navigationSubscription) {
      this.navigationSubscription.unsubscribe();
    }
  }

  resetForm() {
    this.frm.resetForm();
    this.model = new Expenses();

    this.fundoComponent.resetForm();
    this.fundoComponent.portfolio = new Portfolio();

    this.annotationComponent.annotation = null;

    this.favoredComponent.favoredModel = new Favored();
    this.favoredComponent.resetForm();
    this.model.isNewFavored = false;

    this.isEditing = false;
    this.canValidate = false;
    this.model.id = null;
    this.model.settlementTypeId = SettlementType.CETIP;

    this.model.operationDate = new Date();

    this.isSubmit = false;
    this.dataChanged = new EventEmitter<string>();
    this.setEnable(true);
  }
  setTempId(tempId) {
    this.model.tempTicketId = tempId;
  }
  setEnable(enabled: boolean = false) {
    setTimeout(() => {
      if (enabled) {
        this.fundoComponent.setEnable(true);
        this.favoredComponent.setEnable(true);
        Object.values(this.frm.controls).forEach((c) => {
          c.enable();
        });
      } else if (!this.canEdit()) {
        this.fundoComponent.setEnable(false);
        this.favoredComponent.setEnable(false);
        this.annotationComponent.annotationIsDisabled = true;
        Object.values(this.frm.controls).forEach((c) => {
          c.disable();
        });
      }
    }, 10);
  }

  getTicket(idBoleta: number = null): void {
    this.model = new Expenses();

    this.service.ListExpenses().subscribe((f) => {
      this.expensesType = f;
    });
    this.ListSettlementTypes = this.service.ListExpensesSettlementTypes();

    const id =
      idBoleta === null ? +this.route.snapshot.paramMap.get("id") : idBoleta;
    this.model.id = id;
    this.model.settlementTypeId = this.ListSettlementTypes[0].id;

    if (id) {
      this.service.Expenses.getTicket(id).subscribe(
        (q) => {
          Object.assign(this.model, q);
          this.model.portfolioDocument = this.validator.formatDocument(
            this.model.portfolioDocument
          );
          this.model.favoredDocument = this.validator.formatDocument(
            this.model.favoredDocument
          );

          this.model.operationDate = new Date(
            this.model.operationDate
              .toString()
              .replace(/(\d{2})\/(\d{2})\/(\d{4})/, "$2/$1/$3")
          );
          this.model.operationDate.setHours(0, 0, 0, 0);

          if (this.model.issueDate != null) {
            this.model.issueDate = this.datePipe.transform(
              this.model.issueDate,
              "dd/MM/yyyy"
            ) as any;
          }

          if (this.model.settlementDate != null) {
            this.model.settlementDate = this.datePipe.transform(
              this.model.settlementDate,
              "dd/MM/yyyy",
              "UTC/GMT"
            ) as any;
          }

          // if (this.model.operationValue != null) {
          //   this.model.operationValue = this.decimalPipe.transform(this.model.operationValue, '1.0-4', 'pt-BR') as any;
          // }

          this.fundoComponent.fillComponentFieldsLoad(this.model);
          this.favoredComponent.fillComponentFieldsLoad(this.model);

          this.isEditing = true;
          this.canValidate = true;

          this.dataChanged.emit("");

          this.setEnable(false);
        },
        () => this.router.navigate([MenuConstants.Tickets.Expenses])
      );
    } else {
      this.model.operationTypeId = OperationType.Application;
      this.model.settlementDate = this.datePipe.transform(
        new Date(),
        "dd/MM/yyyy",
        "UTC/GMT"
      ) as any;

      if (!this.model.statusId) {
        const start = new Date();
        start.setHours(0, 0, 0, 0);

        this.model.operationDate = start;
      }
    }
  }

  submit(): void {
    this.loaderService.addLoader();

    const editing = this.isEditing;

    var hasAttachment = false;

    var expenseSelected = this.expensesType.find(
      (x) => x.id == this.model.expenseTypeId
    );

    var needsAttachment = expenseSelected?.attachmentIsRequired ?? false;

    if (needsAttachment) {
      this.fileService
        .getFilesPaginatedAttachment(this.model.tempTicketId, this.model.id)
        .subscribe((files) => {
          hasAttachment = files != null && files.rowCount > 0;
          if (!hasAttachment) {
            this.snackBarService.open("É obrigatório incluir um anexo.", "OK", {
              verticalPosition: "top",
              horizontalPosition: "right",
            });
          } else {
            this.register(this.frm)
              .pipe(
                catchError((err) => {
                  this.snackBarService.open(err, "OK", {
                    verticalPosition: "top",
                    horizontalPosition: "right",
                  });
                  return throwError(() => err);
                })
              )
              .subscribe(
                (r) => {
                  const msg = editing
                    ? "Boleta alterada."
                    : "Boleta cadastrada.";

                  this.snackBarService.open(msg, "OK", {
                    verticalPosition: "top",
                    horizontalPosition: "right",
                  });

                  this.isSubmit = false;
                },
                () => {}
              );
          }
        });
    } else {
      this.register(this.frm)
        .pipe(
          catchError((err) => {
            this.snackBarService.open(err, "OK", {
              verticalPosition: "top",
              horizontalPosition: "right",
            });
            return throwError(() => err);
          })
        )
        .subscribe(
          (r) => {
            const msg = editing ? "Boleta alterada." : "Boleta cadastrada.";

            this.snackBarService.open(msg, "OK", {
              verticalPosition: "top",
              horizontalPosition: "right",
            });

            this.isSubmit = false;
          },
          () => {}
        );
    }

    this.loaderService.removeLoader();
  }

  register(f: NgForm, duplicate: boolean = false) {
    const editing = this.isEditing;

    if (!this.canEdit() && !duplicate) {
      return;
    }

    Object.values(f.controls).forEach((c) => {
      c.markAsTouched();
      c.updateValueAndValidity();
    });

    this.canValidate = true;

    const errors = this.getErrors();
    this.fundoComponent.validatePortfolioFields();
    const fundErrors = this.fundoComponent.getErrors();
    const favoredErrors = this.favoredComponent.getErrors();

    if (f.invalid || errors.length > 0) {
      this.isSubmit = true;
      return throwError("Verifique os campos destacados.");
    }

    if (fundErrors.length > 0) {
      this.isSubmit = true;
      return throwError("Verifique os campos nos dados do Fundo Investidor.");
    }

    if (favoredErrors.length > 0) {
      this.isSubmit = true;
      return throwError("Verifique os campos nos dados do Favorecido.");
    }

    let data = Object.assign({}, this.model);

    data.expenseTypeId = this.model.expenseTypeId;
    data.expenseType = null;

    data.operationDate = null;
    data.statusId = null;
    data.id = null;
    data.favoredDocument = this.validator.sanitize(data.favoredDocument);

    if (data.settlementDate != null) {
      data.settlementDate = this.validator.getOnlyDate(data.settlementDate);
    }
    if (data.issueDate) {
      data.issueDate = new Date(
        data.issueDate
          .toString()
          .replace(/(\d{2})\/(\d{2})\/(\d{4})/, "$2/$1/$3")
      );
    }

    if (data.operationDate) {
      data.operationDate = new Date(
        data.operationDate
          .toString()
          .replace(/(\d{2})\/(\d{2})\/(\d{4})/, "$2/$1/$3")
      );
    }

    data = this.fundoComponent.fillPortfolioData(
      data,
      this.fundoComponent.portfolio
    );

    data = this.favoredComponent.fillFavoredData(
      data,
      this.favoredComponent.favoredModel
    );

    data.annotations = this.annotationComponent.annotation;

    if (f.invalid) {
      return throwError(() => "Verifique os campos destacados.");
    }

    if (duplicate) {
      data.workflowStartDate = null;
      data.workflowEndDate = null;
    }

    const method =
      editing && !duplicate
        ? this.service.Expenses.updateTicket(this.model.id, data)
        : this.service.Expenses.registerTicket(data);

    return method
      .pipe(
        catchError((err) => {
          return throwError(() => "Erro ao cadastrar a boleta.");
        })
      )
      .pipe(
        map((fixedIncome: Expenses) => {
          if (!editing) {
            this.model.id = fixedIncome.id;
            this.model.operationDate = fixedIncome.operationDate;
            this.model.statusId = fixedIncome.statusId;
            this.model.accountManagerName = fixedIncome.accountManagerName;
            this.model.accountManagerDocument =
              fixedIncome.accountManagerDocument;
            this.model.ticketCutoffTypeId = fixedIncome.ticketCutoffTypeId;
            this.model.portfolioFundId = fixedIncome.portfolioFundId;

            this.isEditing = true;

            const url = this.router.createUrlTree([this.model.id], {
              relativeTo: this.route,
            });
            this.location.go(url.toString());
          }

          return this.model;
        })
      );
  }

  confirmForward(): Promise<boolean> {
    if (!this.canEdit()) {
      return Promise.resolve(false);
    }

    var textConfirm = 'A boleta não poderá mais ser editada, deseja continuar?';

    if(this.isNotProduction) {
      textConfirm += '<br />ATENÇÃO: ESTE É UM AMBIENTE DE TESTE. NÃO EXECUTE OPERAÇÕES REAIS.';
    }

    return this.dialogService
      .confirm(
        "Encaminhar Boleta",
        textConfirm
      )
      .toPromise()
      .then((result) => {
        if (!result) {
          return false;
        }

        const operationDate = DateTime.fromJSDate(
          new Date(this.model.operationDate.toString())
        );

        if (operationDate.day < DateTime.local().day) {
          return this.dialogService
            .confirm(
              "Boleta Retroativa",
              "A data de operação da boleta será alterada para a data de hoje.",
              "OK",
              "Cancelar"
            )
            .toPromise();
        }

        return true;
      });
  }

  async forward(): Promise<void> {
    const cutoffPermission = await this.checkPermission();

    if (!cutoffPermission.isAllowed) {
      this.snackBarService.open(
        this.cutoffService.warningMessage +
          this.cutoffService.dateTimeOffsetToTimeString(
            cutoffPermission.timeLimit
          ),
        "OK"
      );
      return;
    }

    this.confirmForward().then((ok) => {
      if (!ok) {
        return;
      }

      var hasAttachment = false;

      var expenseSelected = this.expensesType.find(
        (x) => x.id == this.model.expenseTypeId
      );

      var needsAttachment = expenseSelected?.attachmentIsRequired ?? false;

      if (needsAttachment) {
        this.fileService
          .getFilesPaginatedAttachment(this.model.tempTicketId, this.model.id)
          .subscribe((files) => {
            hasAttachment = files != null && files.rowCount > 0;
            if (!hasAttachment) {
              this.snackBarService.open(
                "É obrigatório incluir um anexo.",
                "OK",
                {
                  verticalPosition: "top",
                  horizontalPosition: "right",
                }
              );
            } else {
              this.register(this.frm)
                .pipe(
                  catchError((err) => {
                    this.snackBarService.open(err, "OK", {
                      verticalPosition: "top",
                      horizontalPosition: "right",
                    });
                    return throwError(err);
                  })
                )
                .subscribe(
                  (fixedIncome: Expenses) => {
                    this.service.Expenses.forwardTicket(
                      fixedIncome.id
                    ).subscribe((res) => {
                      this.model.statusDescription = res.statusDescription;

                      this.setEnable(false);

                      this.snackBarService.open("Boleta encaminhada.", "OK", {
                        verticalPosition: "top",
                        horizontalPosition: "right",
                      });

                      this.getTicket(fixedIncome.id);
                    });
                  },
                  () => {}
                );
            }
          });
      } else {
        this.register(this.frm)
          .pipe(
            catchError((err) => {
              this.snackBarService.open(err, "OK", {
                verticalPosition: "top",
                horizontalPosition: "right",
              });
              return throwError(err);
            })
          )
          .subscribe(
            (fixedIncome: Expenses) => {
              this.service.Expenses.forwardTicket(fixedIncome.id).subscribe(
                (res) => {
                  this.model.statusDescription = res.statusDescription;

                  this.setEnable(false);

                  this.snackBarService.open("Boleta encaminhada.", "OK", {
                    verticalPosition: "top",
                    horizontalPosition: "right",
                  });

                  this.getTicket(fixedIncome.id);
                }
              );
            },
            () => {}
          );
      }
    });
  }

  cancel() {
    return this.dialogService
      .confirm("Cancelar Boleta", "Deseja cancelar esta boleta?")
      .pipe(
        flatMap((ok) => {
          if (ok) {
            return this.service.Expenses.cancelDraftTicket(this.model.id);
          }

          return of(null);
        })
      )
      .subscribe((res) => {
        if (res) {
          this.model.statusDescription = res.statusDescription;

          Object.values(this.frm.controls).forEach((c) => {
            c.disable();
          });

          this.snackBarService.open("Boleta cancelada.", "OK", {
            verticalPosition: "top",
            horizontalPosition: "right",
          });

          this.getTicket(this.model.id);
        }
      });
  }

  approve(): void {
    if (this.hasErrorElectronicTransfer()) {
      this.dialogService
        .confirm(
          "Aprovar Boleta",
          "ATENÇÃO: O status da Transferência TED/TEF nesta boleta está com erro. Isto significa que a transferência não aconteceu e a liquidação deve ser feita de forma manual. Deseja aprovar a boleta mesmo assim?",
          "Aprovar mesmo assim",
          "Cancelar",
          DialogSize.Medium
        )
        .subscribe((result) => {
          if (result) {
            this.ticketApprove();
          }
        });
    } else {
      this.dialogService
        .confirm("Aprovar Boleta", "Deseja aprovar esta boleta?")
        .subscribe((result) => {
          if (result) {
            this.ticketApprove();
          }
        });
    }
  }

  private ticketApprove() {
    this.service.approve(this.model.id, "").subscribe(() => {
      this.snackBarService.open("Boleta atualizada com sucesso.");

      this.getTicket(this.model.id);
    });
  }

  reject(): void {
    this.dialogService
      .reject("Reprovar Boleta", "Deseja reprovar esta boleta?")
      .subscribe((result) => {
        if (result) {
          this.service
            .reject(this.model.id, result.justification)
            .subscribe(() => {
              this.snackBarService.open(
                "Boleta atualizada com sucesso.",
                "OK",
                {
                  verticalPosition: "top",
                  horizontalPosition: "right",
                }
              );
              this.getTicket(this.model.id);
            });
        }
      });
  }

  coalesce(value: any) {
    return value || "";
  }

  hasError(prop: string): boolean {
    if (!this.canValidate) {
      return false;
    }

    return !this.validators[prop](this.model[prop]);
  }

  getErrors(): string[] {
    const validationErrors = [];

    Object.keys(this.validators).forEach((k) => {
      if (!this.validators[k](this.model[k])) {
        validationErrors.push(k);
      }
    });

    return validationErrors;
  }

  duplicate(): void {
    this.register(this.frm, true).subscribe((newTicket) => {
      this.snackBarService
        .open("Boleta duplicada.", "Visualizar no Monitor", {
          verticalPosition: "top",
          horizontalPosition: "right",
          duration: 6000,
        })
        .onAction()
        .subscribe((a) => {
          this.router.navigate([
            MenuConstants.Todos,
            { ticketId: TicketType.CETIP },
          ]);
        });
    });
  }

  disableActionButtons() {
    let disabled =
      this.model.transferStatusDescription == "Transferência em Trânsito" ||
      this.model.transferStatusDescription == "Transferência Pendente" ||
      this.model.transferStatusDescription == "Transferência Agendada";

    return disabled;
  }

  hasErrorElectronicTransfer() {
    return this.model.transferStatusDescription == "Transferência com Erro";
  }

  canEdit() {
    return (
      !this.model.statusId &&
      !this.model.statusDescription &&
      this.authService.hasRole(Roles.CreateExpensesTicket)
    );
  }

  canViewBarCode() {
    return this.fundoComponent.portfolio.bank == "407";
  }

  async checkPermission(): Promise<TicketCutoffPermission> {
    let cutoffTypeId = this.model.ticketCutoffTypeId;
    let accountManagerId = this.model.accountManagerId;
    let fundId = this.model.portfolioFundId;

    if (accountManagerId === null) {
      cutoffTypeId = TicketCutoffTypeEnum.Expenses;
      accountManagerId = this.authService.authToken.accountManagerId;
      fundId = this.fundoComponent.portfolio.fundId;
    }
    return this.cutoffService.isAvailable(
      cutoffTypeId,
      this.model.id,
      accountManagerId,
      fundId
    );
  }

  canDuplicate() {
    const _operationDate = this.datePipe.transform(
      this.model.operationDate,
      "yyyy-MM-dd"
    );
    const _duplicateTicketDateLimit = this.datePipe.transform(
      new Date(environment.duplicateTicketDateLimit),
      "yyyy-MM-dd"
    );

    return (
      this.model.id &&
      this.authService.hasRole(Roles.CreateExpensesTicket) &&
      !(_operationDate <= _duplicateTicketDateLimit)
    );
  }

  canChangeStatus() {
    return (
      this.model.statusId &&
      this.authService.hasRoles(this.model.statusRequiredRoles)
    );
  }

  canCancel() {
    return (
      this.model.id &&
      (!this.model.statusId || this.model.statusId === TicketStatus.Draft) &&
      this.authService.hasRole(Roles.CreateExpensesTicket)
    );
  }

  showTransferStatus() {
    return (
      this.model.id != null &&
      this.model.id != 0 &&
      this.model.transferStatusDescription != null &&
      this.model.transferStatusDescription != ""
    );
  }
}
